import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { listAccounts, type AccountSummary } from "@/lib/brokerAccounts";

// Header account search (typeahead). Debounces a broker account lookup
// (GET /api/broker/accounts?search=) and shows matches in a dropdown; picking one
// navigates to its detail. Own-scoped server-side (the API only returns the
// caller's brokerage's accounts). Gated by account:view in the shell, so it's
// only mounted for brokers who can view accounts. A failed search is non-fatal
// (NN #7) — it just shows "No accounts found". Its name says "all accounts"
// (BRK-S074) because pages underneath it carry account searches of their own;
// sharing one accessible name makes the two indistinguishable.
export function HeaderSearch() {
  const { authedRequest } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<AccountSummary[] | null>(null);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const q = query.trim();

  // Debounced search once the query is meaningful (≥ 2 chars).
  useEffect(() => {
    if (q.length < 2) {
      setResults(null);
      return;
    }
    let active = true;
    setResults(null);
    const t = setTimeout(() => {
      listAccounts(authedRequest, { search: q, limit: 6 })
        .then((page) => {
          if (active) {
            setResults(page.items);
            setOpen(true);
          }
        })
        .catch(() => {
          if (active) setResults([]);
        });
    }, 250);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [q, authedRequest]);

  // Close on Escape or an outside click while open.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  function choose(id: string) {
    navigate(`/accounts/${id}`);
    setOpen(false);
    setQuery("");
    setResults(null);
  }

  return (
    <div ref={containerRef} className="relative w-full max-w-md">
      <span
        aria-hidden="true"
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-brk-dim"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <circle
            cx="11"
            cy="11"
            r="7"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="m20 20-3.2-3.2"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      </span>
      <input
        type="search"
        role="searchbox"
        aria-label="Search all accounts"
        autoComplete="off"
        value={query}
        placeholder="Search all accounts…"
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          if (results) setOpen(true);
        }}
        className="h-8 w-full rounded-md border border-brk-border bg-brk-bg pl-9 pr-3 text-sm text-brk-text placeholder:text-brk-dim focus:border-brk-accent focus:bg-brk-panel focus:outline-none"
      />

      {open && q.length >= 2 && (
        <ul
          role="listbox"
          className="absolute z-40 mt-1 max-h-80 w-full overflow-auto rounded-md border border-brk-border bg-brk-panel py-1 text-brk-text shadow-lg"
        >
          {results === null ? (
            <li className="px-3 py-2 text-sm text-brk-dim">Searching…</li>
          ) : results.length === 0 ? (
            <li className="px-3 py-2 text-sm text-brk-dim">
              No accounts found
            </li>
          ) : (
            results.map((a) => (
              <li key={a.id}>
                <button
                  type="button"
                  onClick={() => choose(a.id)}
                  className="flex w-full flex-col gap-0.5 px-3 py-2 text-left transition-colors hover:bg-brk-bg"
                >
                  <span className="text-sm font-medium text-brk-text">
                    {a.legalName}
                  </span>
                  <span className="text-xs text-brk-dim">
                    {a.dbaName ? `${a.dbaName} · ` : ""}#{a.accountNumber}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
